/**
 * Vector path math — Tier 2 Group A foundation.
 *
 * Pure functions that operate on the `VectorPath` data shape from
 * `types/image-editor.types`. No DOM, no React, no Fabric.
 *
 * Three responsibilities live here:
 *   1. SVG path-data serialization (`pathToSvgData`) — converts
 *      VectorPath into the string fed to `fabric.Path`. The
 *      renderer is the single consumer; the workspace's
 *      `useFabricCanvas` rebuilds the Fabric.Path from the
 *      document state on every relevant change.
 *   2. Curvature-pen tangent computation
 *      (`recomputeCurvatureHandles`) — given a list of anchor
 *      positions, fills in each anchor's `in/out` tangent handles
 *      so the resulting path is C1-continuous (smooth) through
 *      every anchor. Catmull-Rom-style: each anchor's tangent
 *      points from its previous neighbor toward its next neighbor.
 *   3. Hit-testing helpers (`hitTestAnchor`, `hitTestHandle`,
 *      `hitTestSegment`) — used by Path Edit mode in a follow-up
 *      PR. Shipped now so the API surface is locked + tested.
 *
 * Path Edit mode (PR-B) will add anchor type cycling and segment-
 * split / segment-merge ops; the foundation is sufficient for the
 * pen-tool creation flow this PR delivers.
 */

import type { AnchorType, VectorAnchor, VectorPath, VectorSubPath } from '../types';

/** Build an empty path with one open sub-path. The pen-tool
 *  creation flow seeds with this and pushes anchors as the user
 *  clicks. */
export function emptyPath(): VectorPath {
  return { subPaths: [{ closed: false, anchors: [] }] };
}

/** Append an anchor to the first sub-path. Returns a fresh
 *  VectorPath; the caller should treat it as immutable (consistent
 *  with how the reducer expects path values). */
export function appendAnchor(path: VectorPath, anchor: VectorAnchor): VectorPath {
  const sub = path.subPaths[0] ?? { closed: false, anchors: [] };
  const nextSub: VectorSubPath = { ...sub, anchors: [...sub.anchors, anchor] };
  const nextSubs = path.subPaths.length > 0 ? path.subPaths.slice() : [nextSub];
  nextSubs[0] = nextSub;
  return { ...path, subPaths: nextSubs };
}

/** Mark the first sub-path as closed (the renderer emits `Z`).
 *  Idempotent — no-op when already closed. */
export function closeFirstSubPath(path: VectorPath): VectorPath {
  const sub = path.subPaths[0];
  if (!sub || sub.closed) return path;
  const nextSubs = path.subPaths.slice();
  nextSubs[0] = { ...sub, closed: true };
  return { ...path, subPaths: nextSubs };
}

/**
 * Infer Catmull-Rom-style tangent handles for a SINGLE anchor at
 * `index` within `anchors`, returning a fresh anchor with the
 * handles populated. Used by Path Edit mode's "Smooth" radio
 * (Tier 2 Group A PR-B) — when the user switches a corner anchor
 * to smooth, we infer plausible handles from the prev/next
 * neighbors so the radio change has a visible effect.
 *
 * Mirrors `recomputeCurvatureHandles`'s per-anchor math but
 * scoped to one anchor so we don't disturb the rest of the path's
 * existing handles. Same Catmull-Rom-with-tension convention; same
 * one-sided-collapse for end-anchors of open paths.
 *
 * Edge cases:
 *   - `anchors.length < 2` → returns the anchor unchanged
 *     (single-point path; nothing to infer from).
 *   - End anchor of open path → only the inward-facing handle
 *     gets populated; the outward side stays `undefined`. The
 *     user can drag the inward handle to introduce curvature;
 *     Photoshop has the same constraint at open-path endpoints.
 */
export function populateSmoothAnchorHandles(
  anchors: VectorAnchor[],
  index: number,
  closed: boolean,
  tension = 0.5,
): VectorAnchor {
  const a = anchors[index];
  const n = anchors.length;
  if (n < 2) return a;
  const prevIdx = index === 0 ? (closed ? n - 1 : index) : index - 1;
  const nextIdx = index === n - 1 ? (closed ? 0 : index) : index + 1;
  const prev = anchors[prevIdx];
  const next = anchors[nextIdx];
  const dx = next.x - prev.x;
  const dy = next.y - prev.y;
  const tangentLen = Math.hypot(dx, dy) || 1;
  const tx = dx / tangentLen;
  const ty = dy / tangentLen;
  const distIn = index === 0 && !closed ? 0 : Math.hypot(a.x - prev.x, a.y - prev.y);
  const distOut = index === n - 1 && !closed ? 0 : Math.hypot(next.x - a.x, next.y - a.y);
  const next2: VectorAnchor = { ...a, type: 'smooth' };
  if (distIn > 0) {
    next2.inX = -tx * tension * distIn;
    next2.inY = -ty * tension * distIn;
  } else {
    next2.inX = undefined;
    next2.inY = undefined;
  }
  if (distOut > 0) {
    next2.outX = tx * tension * distOut;
    next2.outY = ty * tension * distOut;
  } else {
    next2.outX = undefined;
    next2.outY = undefined;
  }
  return next2;
}

/**
 * Curvature-pen tangent recomputation. Given a list of anchor
 * POSITIONS, fills in each anchor's `inX/inY/outX/outY` such that
 * the path runs smoothly through every anchor.
 *
 * Algorithm (Catmull-Rom style, tension = 0.5):
 *   For each anchor i in the middle of the run, the tangent
 *   direction is (anchors[i+1] - anchors[i-1]). The outgoing
 *   handle length is set to `tension * |anchor[i+1] - anchor[i]|`
 *   along that tangent; the incoming handle is the mirror.
 *
 *   End anchors (i = 0 and i = N-1) use a one-sided tangent
 *   pointed at their only neighbor.
 *
 *   Closed paths treat the array as cyclic — anchor[-1] wraps to
 *   anchor[N-1] and anchor[N] wraps to anchor[0].
 *
 * The returned anchors are fresh objects (no mutation of input).
 * All handles are stored RELATIVE to their anchor's position
 * (matches the `VectorAnchor` type contract).
 */
export function recomputeCurvatureHandles(
  anchors: { x: number; y: number; type?: AnchorType }[],
  closed: boolean,
  tension = 0.5,
): VectorAnchor[] {
  const n = anchors.length;
  if (n === 0) return [];
  if (n === 1) {
    // Single point — degenerate; no handles needed.
    const a = anchors[0];
    return [{ x: a.x, y: a.y, type: a.type ?? 'smooth' }];
  }
  const out: VectorAnchor[] = [];
  for (let i = 0; i < n; i++) {
    const cur = anchors[i];
    const prevIdx = i === 0 ? (closed ? n - 1 : 0) : i - 1;
    const nextIdx = i === n - 1 ? (closed ? 0 : n - 1) : i + 1;
    const prev = anchors[prevIdx];
    const next = anchors[nextIdx];

    // Tangent direction: vector from prev → next, normalized via
    // segment lengths on either side. For end anchors of an open
    // path, prevIdx === i (or nextIdx === i) so the corresponding
    // component is zero and the tangent collapses to one-sided.
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;

    // Handle lengths are tension * distance to the relevant
    // neighbor — gives perceptually smooth curvature without
    // overshoot for typical click patterns.
    const distIn = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    const distOut = Math.hypot(next.x - cur.x, next.y - cur.y);
    // Direction magnitude — used to normalize the (dx, dy)
    // tangent before scaling by distance + tension.
    const tangentLen = Math.hypot(dx, dy) || 1;
    const tx = dx / tangentLen;
    const ty = dy / tangentLen;

    // For an open-path endpoint with no usable tangent (collapsed
    // to one-sided), both handle distances are zero on the absent
    // side, so the handle vanishes naturally.
    const inHandleLen = i === 0 && !closed ? 0 : tension * distIn;
    const outHandleLen = i === n - 1 && !closed ? 0 : tension * distOut;

    out.push({
      x: cur.x,
      y: cur.y,
      // Incoming handle points BACK toward prev (negative tangent
      // direction); outgoing handle points TOWARD next.
      inX: -tx * inHandleLen || undefined,
      inY: -ty * inHandleLen || undefined,
      outX: tx * outHandleLen || undefined,
      outY: ty * outHandleLen || undefined,
      type: cur.type ?? 'smooth',
    });
  }
  return out;
}

/**
 * Serialize a VectorPath to an SVG path-data string suitable for
 * `new fabric.Path(d, ...)`. Each sub-path emits one `M` (move)
 * followed by per-segment commands; closed sub-paths append `Z`.
 *
 * Per-segment command picker:
 *   - If either endpoint has a tangent handle (out from start OR
 *     in to end), emit a cubic `C` with the two control points.
 *     Missing handles default to the endpoint position itself
 *     (collapses to a degenerate Bezier that traces a line).
 *   - When both endpoints have no handles AT ALL, emit `L`
 *     (saves bytes; renders identically).
 *
 * Coordinates are emitted with up to 3 decimal places — enough for
 * sub-pixel positioning without bloating the data string.
 */
export function pathToSvgData(path: VectorPath): string {
  const parts: string[] = [];
  for (const sub of path.subPaths) {
    if (sub.anchors.length === 0) continue;
    const a0 = sub.anchors[0];
    parts.push(`M ${f(a0.x)} ${f(a0.y)}`);
    for (let i = 1; i < sub.anchors.length; i++) {
      const prev = sub.anchors[i - 1];
      const cur = sub.anchors[i];
      parts.push(segmentCommand(prev, cur));
    }
    if (sub.closed && sub.anchors.length > 1) {
      // Closing segment from last anchor back to the first.
      const last = sub.anchors[sub.anchors.length - 1];
      parts.push(segmentCommand(last, a0));
      parts.push('Z');
    }
  }
  return parts.join(' ');
}

/** Produce the SVG command for the segment from `prev` to `cur`.
 *  Cubic when handles exist on either endpoint; line otherwise. */
function segmentCommand(prev: VectorAnchor, cur: VectorAnchor): string {
  const hasPrevOut = prev.outX !== undefined || prev.outY !== undefined;
  const hasCurIn = cur.inX !== undefined || cur.inY !== undefined;
  if (!hasPrevOut && !hasCurIn) {
    return `L ${f(cur.x)} ${f(cur.y)}`;
  }
  const c1x = prev.x + (prev.outX ?? 0);
  const c1y = prev.y + (prev.outY ?? 0);
  const c2x = cur.x + (cur.inX ?? 0);
  const c2y = cur.y + (cur.inY ?? 0);
  return `C ${f(c1x)} ${f(c1y)} ${f(c2x)} ${f(c2y)} ${f(cur.x)} ${f(cur.y)}`;
}

/** Format a number with up to 3 decimal places, trimming trailing
 *  zeros. `f(3.14000)` → `'3.14'`. Keeps the path-data string
 *  compact without losing sub-pixel precision. */
function f(n: number): string {
  return Number(n.toFixed(3)).toString();
}

/** Bake a Fabric transform (`matrix` from `calcTransformMatrix`)
 *  + `pathOffset` into a `VectorPath`, producing scene-space
 *  coordinates. Used by both Path Edit (so the anchor overlay
 *  renders aligned with the moved/scaled/rotated layer) and
 *  Boolean Ops (so paper.js sees each layer's actual scene
 *  position, not its raw path-data origin).
 *
 *  Coordinate chain (verified against Fabric v7 `Path.mjs`):
 *    pathOffset = (bbox.left + width/2, bbox.top + height/2)
 *    render: ctx.translate(-pathOffset) before drawing pathData
 *      → local (px - pathOffset.x, py - pathOffset.y)
 *    matrix: maps center-relative local point → scene coords
 *  So: scene = matrix * (pathData − pathOffset).
 *
 *  Handle deltas are direction vectors → apply matrix's
 *  rotation/scale portion only (no translation, no pathOffset
 *  shift). */
export function bakeVectorPath(
  path: VectorPath,
  matrix: ReadonlyArray<number>,
  pathOffset: { x: number; y: number },
): VectorPath {
  const bakeAnchor = (a: VectorAnchor): VectorAnchor => {
    const cx = a.x - pathOffset.x;
    const cy = a.y - pathOffset.y;
    const sx = matrix[0] * cx + matrix[2] * cy + matrix[4];
    const sy = matrix[1] * cx + matrix[3] * cy + matrix[5];
    const bakeVec = (dx: number, dy: number) => ({
      x: matrix[0] * dx + matrix[2] * dy,
      y: matrix[1] * dx + matrix[3] * dy,
    });
    const next: VectorAnchor = { ...a, x: sx, y: sy };
    if (a.inX !== undefined || a.inY !== undefined) {
      const v = bakeVec(a.inX ?? 0, a.inY ?? 0);
      next.inX = v.x;
      next.inY = v.y;
    }
    if (a.outX !== undefined || a.outY !== undefined) {
      const v = bakeVec(a.outX ?? 0, a.outY ?? 0);
      next.outX = v.x;
      next.outY = v.y;
    }
    return next;
  };
  return {
    ...path,
    subPaths: path.subPaths.map((sp) => ({
      ...sp,
      anchors: sp.anchors.map(bakeAnchor),
    })),
  };
}

// ─── Hit testing ─────────────────────────────────────────────────
// Used by Path Edit mode (PR-B). Shipped now so the API surface is
// locked + tested + ready for the on-canvas overlay's mouse:down
// handler in the follow-up PR.

/** Return the (subPathIndex, anchorIndex) of the first anchor
 *  within `radius` scene-space pixels of (x, y), or null. Searches
 *  sub-paths in order, anchors front-to-back within each. */
export function hitTestAnchor(
  path: VectorPath,
  x: number,
  y: number,
  radius: number,
): { subPathIndex: number; anchorIndex: number } | null {
  const r2 = radius * radius;
  for (let s = 0; s < path.subPaths.length; s++) {
    const anchors = path.subPaths[s].anchors;
    for (let i = 0; i < anchors.length; i++) {
      const a = anchors[i];
      const dx = a.x - x;
      const dy = a.y - y;
      if (dx * dx + dy * dy <= r2) return { subPathIndex: s, anchorIndex: i };
    }
  }
  return null;
}

/** Hit-test the handles of a specific anchor. Returns 'in' / 'out'
 *  / null. Useful when Path Edit knows an anchor is selected and
 *  wants to detect whether the user clicked a handle vs the anchor
 *  itself vs empty space. */
export function hitTestHandle(
  anchor: VectorAnchor,
  x: number,
  y: number,
  radius: number,
): 'in' | 'out' | null {
  const r2 = radius * radius;
  if (anchor.inX !== undefined && anchor.inY !== undefined) {
    const hx = anchor.x + anchor.inX;
    const hy = anchor.y + anchor.inY;
    const dx = hx - x;
    const dy = hy - y;
    if (dx * dx + dy * dy <= r2) return 'in';
  }
  if (anchor.outX !== undefined && anchor.outY !== undefined) {
    const hx = anchor.x + anchor.outX;
    const hy = anchor.y + anchor.outY;
    const dx = hx - x;
    const dy = hy - y;
    if (dx * dx + dy * dy <= r2) return 'out';
  }
  return null;
}

/** Return the segment whose Bézier curve passes within `radius`
 *  scene-space pixels of (x, y), or null. Used by Path Edit's
 *  "click segment to add anchor" interaction. Approximates the
 *  curve via a polyline at `samples` subdivisions — accurate
 *  enough for cursor-radius interactions without solving the cubic
 *  exactly. */
export function hitTestSegment(
  path: VectorPath,
  x: number,
  y: number,
  radius: number,
  samples = 24,
): { subPathIndex: number; segmentIndex: number; t: number } | null {
  const r2 = radius * radius;
  for (let s = 0; s < path.subPaths.length; s++) {
    const sub = path.subPaths[s];
    const segCount = sub.anchors.length - (sub.closed ? 0 : 1);
    for (let i = 0; i < segCount; i++) {
      const a = sub.anchors[i];
      const b = sub.anchors[(i + 1) % sub.anchors.length];
      const c1x = a.x + (a.outX ?? 0);
      const c1y = a.y + (a.outY ?? 0);
      const c2x = b.x + (b.inX ?? 0);
      const c2y = b.y + (b.inY ?? 0);
      for (let k = 0; k < samples; k++) {
        const t = (k + 0.5) / samples;
        const omt = 1 - t;
        const bx =
          omt * omt * omt * a.x + 3 * omt * omt * t * c1x + 3 * omt * t * t * c2x + t * t * t * b.x;
        const by =
          omt * omt * omt * a.y + 3 * omt * omt * t * c1y + 3 * omt * t * t * c2y + t * t * t * b.y;
        const dx = bx - x;
        const dy = by - y;
        if (dx * dx + dy * dy <= r2) {
          return { subPathIndex: s, segmentIndex: i, t };
        }
      }
    }
  }
  return null;
}
